// frontend/src/components/TrialBanner.jsx
// Surfaces the same access state the backend enforces, so "why did my save
// just fail?" has an answer on screen instead of only a 402 in the network tab.
//
// It reads company.access, which is computed by services/accessState.js — the
// same code that decides whether to block the write. It used to do its own date
// arithmetic against trial_ends_at, which meant it only knew about trials: once
// billing gained a due date, a paying customer with an overdue invoice was told
// their TRIAL had ended, which is both wrong and alarming.
import React, { useState } from 'react';
import { Alert, Collapse } from '@mui/material';

const fmt = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : '');

const plural = (n) => (Math.abs(n) === 1 ? 'day' : 'days');

export default function TrialBanner({ company }) {
  const [dismissed, setDismissed] = useState(false);
  const access = company?.access;

  // No state from the server means an older session; say nothing rather than
  // guess, which is what the old version did wrong.
  if (!access || access.state === 'ok') return null;

  // ── Hard states: not dismissible, because they explain why a save failed ──
  if (access.state === 'suspended') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        This account has been suspended. Please contact Narevek to restore access.
      </Alert>
    );
  }

  if (access.state === 'trial_expired') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Your trial ended on {fmt(access.trial_ends_at)}. You can still view and export your data,
        but creating, editing or deleting anything is disabled until Narevek upgrades your plan.
      </Alert>
    );
  }

  if (access.state === 'payment_overdue') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Your subscription payment was due on {fmt(access.next_due_date)} — {access.days} {plural(access.days)} ago.
        You can still view and export your data, but creating, editing or deleting anything is
        disabled until the payment is recorded. The account is suspended entirely after{' '}
        {access.suspend_after_days} days overdue, so please contact Narevek.
      </Alert>
    );
  }

  // ── Warnings: still writable, so these can be dismissed ──
  if (access.state === 'trial_ending') {
    return (
      <Collapse in={!dismissed}>
        <Alert severity="warning" onClose={() => setDismissed(true)} sx={{ mb: 2 }}>
          Your trial ends in {access.days} {plural(access.days)} ({fmt(access.trial_ends_at)}).
          Contact Narevek to upgrade before it lapses into read-only mode.
        </Alert>
      </Collapse>
    );
  }

  if (access.state === 'payment_due') {
    // days is positive once past the due date, negative while it is still ahead.
    const overdue = access.days > 0;
    return (
      <Collapse in={!dismissed}>
        <Alert severity="warning" onClose={() => setDismissed(true)} sx={{ mb: 2 }}>
          {overdue
            ? `Your subscription payment was due on ${fmt(access.next_due_date)}. Changes stay
               available for ${access.grace_days - access.days} more ${plural(access.grace_days - access.days)},
               then the account becomes read-only.`
            : `Your subscription payment is due on ${fmt(access.next_due_date)}.`}
        </Alert>
      </Collapse>
    );
  }

  return null;
}
